/**
 * Aircraft details from the national aircraft registers (docs/specs/aircraft-details.md; investigation
 * docs/reference/investigations/aircraft-details-and-windy-embed.md; MB decisions 327-335).
 *
 * - Registers (decision 334): Australia (CASA, weekly, decision 331), New Zealand (CAA), Papua New Guinea (CASA PNG,
 *   a PDF), the USA (FAA) and Canada (Transport Canada). Each is imported in full, addresses included (decision 328),
 *   into Teslyn Cloud's platform table `aircraft_register` by the import job (tools/registers).
 * - Owner and operator names are shown to every staff role of the tenant and never to clients (decision 327). The
 *   database decides (`aircraft_register_lookup`); `profileFromRegister` drops them again when told to.
 * - adsbdb is asked through Teslyn's server for foreign aircraft only, for type and country only (decisions 330, 333),
 *   with an honest User-Agent; its owner field is never kept.
 * - RAAus marks get a "Search on RAAus" browser link, never a call (decision 329).
 *
 * This file has no imports on purpose: the `aircraft-details` Edge Function (Deno) uses an identical copy,
 * `supabase/functions/_shared/aircraft-register.ts` (tools/dev/copy-edge-shared.mjs), and
 * test/integrations.test.ts fails if the two differ.
 */

// ---------------------------------------------------------------------------------------------- the registers

export type RegisterId = 'casa' | 'nzcaa' | 'casapng' | 'faa' | 'tcca';

export interface RegisterSource {
  readonly id: RegisterId;
  /** The register's name on the card's source line. */
  readonly name: string;
  readonly country: string;
  /** Where the import job downloads it. */
  readonly url: string;
  readonly format: 'csv_zip' | 'csv' | 'pdf' | 'faa_zip' | 'tcca_zip';
  /** Days between imports. */
  readonly refreshDays: number;
  /** The licence or terms that apply, as read on 20 Oct 2026 (docs/specs/aircraft-details.md §3). */
  readonly licence: string;
  /** The credit the card and the privacy policy give. */
  readonly attribution: string;
  /** Whether the terms need the authority's written permission for Teslyn's use. */
  readonly permission: 'not_needed' | 'required';
  /** The date the permission was recorded (decisions-mb.md), or null while it is not. */
  readonly permissionRecorded: string | null;
  /** False when the authority's server refuses automated downloads (the FAA): import a file saved in a browser. */
  readonly automatedDownload: boolean;
}

export const REGISTERS: readonly RegisterSource[] = [
  {
    id: 'casa',
    name: 'Australian Civil Aircraft Register, CASA',
    country: 'Australia',
    url: 'https://services.casa.gov.au/CSV/acrftreg.zip',
    format: 'csv_zip',
    refreshDays: 7,
    licence:
      'CASA website material under Creative Commons Attribution 4.0 International (casa.gov.au copyright page); register information published under CASR 47.030. Coverage of the data file to be confirmed by CASA (letter drafted).',
    attribution: 'Australian Civil Aircraft Register, Civil Aviation Safety Authority (CC BY 4.0)',
    permission: 'not_needed',
    permissionRecorded: null,
    automatedDownload: true,
  },
  {
    id: 'nzcaa',
    name: 'NZ Register of Aircraft, Civil Aviation Authority of New Zealand',
    country: 'New Zealand',
    url: 'https://www.aviation.govt.nz/assets/aircraft/aircraft-register/Aircraft-Register-for-website.csv',
    format: 'csv',
    refreshDays: 7,
    licence:
      'aviation.govt.nz copyright: reproduction for personal use without permission, the CAA acknowledged as the source; any other use needs permission (info@caa.govt.nz).',
    attribution: 'NZ Register of Aircraft, Civil Aviation Authority of New Zealand',
    permission: 'required',
    permissionRecorded: null,
    automatedDownload: true,
  },
  {
    id: 'casapng',
    name: 'PNG Aircraft Register, CASA PNG',
    country: 'Papua New Guinea',
    url: 'https://casapng.gov.pg/admin/images/13Aug2026-PNG-AC-Register.pdf',
    format: 'pdf',
    refreshDays: 30,
    licence:
      'No licence, copyright or terms page on casapng.gov.pg (read 20 Oct 2026), only the footer "© Civil Aviation Safety Authority of Papua New Guinea 2026"; the register is published by the authority as a public PDF. Loaded by decision 334; CASA PNG asked for confirmation and a data file (letter drafted).',
    attribution: 'PNG Aircraft Register, Civil Aviation Safety Authority of Papua New Guinea',
    permission: 'not_needed',
    permissionRecorded: null,
    automatedDownload: true,
  },
  {
    id: 'faa',
    name: 'FAA Aircraft Registry, releasable aircraft',
    country: 'United States of America',
    url: 'https://registry.faa.gov/database/ReleasableAircraft.zip',
    format: 'faa_zip',
    refreshDays: 7,
    licence:
      'A work of the US Government, public domain in the US (17 U.S.C. 105). Owners who asked the FAA to withhold their details (49 U.S.C. 44114(b)) are blank in the file and stay blank. registry.faa.gov refuses automated downloads (HTTP 403): the file is saved in a browser and imported with --file.',
    attribution: 'FAA Aircraft Registry (releasable aircraft database)',
    permission: 'not_needed',
    permissionRecorded: null,
    automatedDownload: false,
  },
  {
    id: 'tcca',
    name: 'Canadian Civil Aircraft Register, Transport Canada',
    country: 'Canada',
    url: 'https://wwwapps.tc.gc.ca/Saf-Sec-Sur/2/CCARCS-RIACC/download/ccarcsdb.zip',
    format: 'tcca_zip',
    refreshDays: 7,
    licence:
      'Government of Canada Open Data Licence Agreement for Unrestricted Use of Canada’s Data (click-through on the CCAR download page, not OGL-Canada): commercial use allowed, but 3.1(c) needs a written agreement for every redistribution and 3.1(d) forbids linking the data to identify an individual; wwwapps.tc.gc.ca/robots.txt disallows all automated access. Off until Transport Canada agrees in writing (letter drafted).',
    attribution:
      'Includes data provided by the Government of Canada (Canadian Civil Aircraft Register, Transport Canada). Not an endorsement by the Government of Canada.',
    permission: 'required',
    permissionRecorded: null,
    automatedDownload: false,
  },
];

export function registerSource(id: RegisterId): RegisterSource {
  return REGISTERS.find((r) => r.id === id)!;
}

/** Why a register may not be imported yet, or null when it may (NZ CAA: until its permission is recorded). */
export function registerImportBlocked(id: RegisterId): string | null {
  const r = registerSource(id);
  if (r.permission === 'required' && !r.permissionRecorded)
    return `${r.name}: its terms need written permission for Teslyn's use, and none is recorded yet. The import stays off until it is.`;
  return null;
}

// ---------------------------------------------------------------------------------------------- rows

export type AirframeClass =
  'aeroplane' | 'helicopter' | 'gyroplane' | 'glider' | 'balloon' | 'airship' | 'drone' | 'other';

export const AIRFRAME_WORD: Readonly<Record<AirframeClass, string>> = {
  aeroplane: 'Aeroplane',
  helicopter: 'Helicopter',
  gyroplane: 'Gyroplane',
  glider: 'Glider',
  balloon: 'Balloon',
  airship: 'Airship',
  drone: 'Remotely piloted aircraft',
  other: 'Other',
};

/** A registration holder (owner) or registered operator, as the register gives it. */
export interface RegisterParty {
  readonly name: string;
  /** Street or postal address lines, joined. */
  readonly address: string | null;
  readonly suburb: string | null;
  readonly state: string | null;
  readonly postcode: string | null;
  readonly country: string | null;
}

/** One aircraft of one register (the platform table `aircraft_register`). */
export interface RegisterRow {
  readonly register: RegisterId;
  /** The full mark: VH-JEI, ZK-HAA, P2-PHU, N12345, C-FAAS, CF-AAN. */
  readonly mark: string;
  /** Six hex digits, lower case, when the register gives the Mode S address. */
  readonly icao_hex: string | null;
  readonly manufacturer: string | null;
  readonly model: string | null;
  readonly icao_type: string | null;
  readonly airframe: AirframeClass | null;
  /** The register's own words for the airframe. */
  readonly airframe_text: string | null;
  readonly serial: string | null;
  readonly year_built: number | null;
  readonly mtow_kg: number | null;
  readonly engine: string | null;
  readonly status: string | null;
  readonly suspended: boolean;
  /** ISO dates. */
  readonly first_registered: string | null;
  readonly expires: string | null;
  /** The register's country. */
  readonly country: string;
  readonly holder: RegisterParty | null;
  readonly operator: RegisterParty | null;
  /** Every field of the source record (decision 328: imported in full). */
  readonly raw: Readonly<Record<string, unknown>>;
}

export interface ParsedRegister {
  readonly rows: readonly RegisterRow[];
  /** Rows left out, in words. */
  readonly problems: readonly string[];
  /** The row count the file states about itself (Transport Canada's trailer, the PNG PDF's total). */
  readonly statedRows?: number;
}

// ---------------------------------------------------------------------------------------------- CSV and text

/** RFC 4180 CSV: quoted fields, doubled quotes, commas and newlines inside quotes, CRLF or LF, a leading BOM. */
export function registerCsvRows(text: string): string[][] {
  return [...registerCsvIter(text)];
}

const CR = String.fromCharCode(13);
const LF = String.fromCharCode(10);

/** The same rows one at a time, slicing fields out of the text (the FAA's MASTER file is about 200 MB). */
export function* registerCsvIter(text: string): Generator<string[]> {
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const n = s.length;
  let i = 0;
  let row: string[] = [];
  while (i < n) {
    let field: string;
    if (s[i] === '"') {
      let j = i + 1;
      let out = '';
      for (;;) {
        const q = s.indexOf('"', j);
        if (q < 0) {
          out += s.slice(j);
          j = n;
          break;
        }
        out += s.slice(j, q);
        if (s[q + 1] === '"') {
          out += '"';
          j = q + 2;
        } else {
          j = q + 1;
          break;
        }
      }
      field = out;
      i = j;
    } else {
      let j = i;
      while (j < n) {
        const c = s.charCodeAt(j);
        if (c === 44 || c === 10 || c === 13) break;
        j += 1;
      }
      field = s.slice(i, j);
      i = j;
    }
    row.push(field);
    const c = s[i];
    if (c === ',') {
      i += 1;
      if (i === n) row.push('');
    } else {
      // end of line or of text
      if (c === CR && s[i + 1] === LF) i += 2;
      else if (c === CR || c === LF) i += 1;
      yield row;
      row = [];
    }
  }
}

const UPPER_WORDS: Readonly<Record<string, string>> = {
  PTY: 'Pty',
  LTD: 'Ltd',
  CO: 'Co',
  ST: 'St',
  MT: 'Mt',
  DR: 'Dr',
  RD: 'Rd',
  GMBH: 'GmbH',
  MBH: 'mbH',
  LLC: 'LLC',
  BV: 'BV',
};

/**
 * Title case for names written in capitals (CASA, CASA PNG): `THOMSON AIRBORNE PTY LTD` → `Thomson Airborne Pty Ltd`.
 * A short word without vowels (DG, RFDS, DC) is probably initials and stays; words with digits and words already in
 * mixed case stay as written.
 */
export function registerTitleCase(raw: string, acronyms = false): string {
  return raw.replace(/[A-Za-z0-9'’]+/g, (w) => {
    if (/[a-z]/.test(w) || /\d/.test(w)) return w;
    const known = UPPER_WORDS[w];
    if (known) return known;
    if (w.length <= 4 && !/[AEIOUY]/.test(w)) return w;
    if (acronyms && w.length <= 3 && !COMMON_SHORT.has(w)) return w;
    return w
      .toLowerCase()
      .replace(/^[a-z]/, (c) => c.toUpperCase())
      .replace(/(['’])([a-z])/g, (_m, q: string, c: string) => q + c.toUpperCase());
  });
}

/** Short words that are words, not a manufacturer's initials (ATR, PAC, GAF stay as written). */
const COMMON_SHORT = new Set([
  'THE',
  'AND',
  'OF',
  'FOR',
  'AIR',
  'SKY',
  'NEW',
  'BAY',
  'ONE',
  'TOP',
  'SUN',
  'AT',
  'DE',
  'LA',
  'LE',
  'VON',
  'VAN',
  'DER',
  'DU',
  'DI',
]);

const CORPORATE = new Set([
  'aircraft',
  'aviation',
  'company',
  'co',
  'corp',
  'corporation',
  'inc',
  'incorporated',
  'ltd',
  'limited',
  'pty',
  'gmbh',
  'kg',
  'llc',
  'sa',
  'ag',
  'plc',
  '&',
  'and',
]);

/** The manufacturer as people say it: corporate suffixes stripped, title case (`CESSNA AIRCRAFT COMPANY` → Cessna). */
export function manufacturerName(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim();
  if (!t) return null;
  const words = registerTitleCase(t, true).split(/\s+/);
  while (words.length > 1) {
    const last = words[words.length - 1]!.replace(/[.,]+$/g, '').toLowerCase();
    if (!CORPORATE.has(last)) break;
    words.pop();
  }
  return words.join(' ').replace(/[,.&\s]+$/g, '') || null;
}

const blank = (s: string | null | undefined): string | null => {
  const t = (s ?? '').trim();
  return t === '' ? null : t;
};

/** DD/MM/YYYY → YYYY-MM-DD, else null. */
function dmy(s: string | null | undefined): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((s ?? '').trim());
  return m ? `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}` : null;
}

/** YYYY/MM/DD or YYYYMMDD → YYYY-MM-DD, else null. */
function ymd(s: string | null | undefined): string | null {
  const m = /^(\d{4})\/?(\d{2})\/?(\d{2})$/.exec((s ?? '').trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function num(s: string | null | undefined): number | null {
  const t = (s ?? '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function year(s: string | null | undefined): number | null {
  const m = /^(\d{4})/.exec((s ?? '').trim());
  return m ? Number(m[1]) : null;
}

const hexOf = (s: string | null | undefined): string | null => {
  const t = (s ?? '').trim().toLowerCase();
  return /^[0-9a-f]{6}$/.test(t) ? t : null;
};

function party(p: {
  name: string | null;
  lines: readonly (string | null | undefined)[];
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}): RegisterParty | null {
  const name = blank(p.name);
  if (!name) return null;
  const address = p.lines.map(blank).filter((l): l is string => l !== null);
  return {
    name: registerTitleCase(name),
    address: address.length ? address.join(', ') : null,
    suburb: blank(p.suburb) ? registerTitleCase(blank(p.suburb)!) : null,
    state: blank(p.state),
    postcode: blank(p.postcode),
    country: blank(p.country),
  };
}

/** The airframe class from any register's words (CASA, NZ CAA, Transport Canada, FAA type codes as words). */
export function airframeClass(text: string | null | undefined): AirframeClass | null {
  const t = (text ?? '').trim().toLowerCase();
  if (!t) return null;
  if (/^rpa\b|remotely piloted|drone|unmanned/.test(t)) return 'drone';
  if (/gyro/.test(t)) return 'gyroplane';
  if (/rotorcraft|helicopter/.test(t)) return 'helicopter';
  if (/glider/.test(t)) return 'glider';
  if (/balloon/.test(t)) return 'balloon';
  if (/airship|blimp|dirigible/.test(t)) return 'airship';
  if (/aeroplane|airplane|fixed wing|microlight|weight-shift|powered parachute/.test(t)) return 'aeroplane';
  return 'other';
}

function byHeader(
  text: string,
  required: readonly string[],
  register: string,
): { header: string[]; records: Record<string, string>[] } {
  let header: string[] | null = null;
  const records: Record<string, string>[] = [];
  for (const r of registerCsvIter(text)) {
    if (!r.some((f) => f.trim() !== '')) continue;
    if (!header) {
      header = r.map((h) => h.trim());
      const missing = required.filter((c) => !header!.includes(c));
      if (missing.length)
        throw new Error(`The ${register} file has changed: its header lacks ${missing.join(', ')}.`);
      continue;
    }
    const o: Record<string, string> = {};
    header.forEach((h, i) => (o[h] = r[i] ?? ''));
    records.push(o);
  }
  if (!header) throw new Error(`The ${register} file is empty.`);
  return { header, records };
}

// ---------------------------------------------------------------------------------------------- CASA

const CASA_REQUIRED = [
  'Mark',
  'Manu',
  'Model',
  'Serial',
  'MTOW',
  'regType',
  'regholdname',
  'regholdSuburb',
  'regholdState',
  'regopName',
  'Airframe',
  'Datefirstreg',
  'suspendstatus',
  'ICAOtypedesig',
];

function engineText(count: string, model: string, type: string): string | null {
  const n = num(count);
  const m = blank(model);
  if (!n || !m || /^not applicable$/i.test(m)) return null;
  const kind = blank(type) && !/^not applicable$/i.test(type) ? ` (${type.trim().toLowerCase()})` : '';
  return `${n} × ${m}${kind}`;
}

/** The CASA register CSV (`acrftreg.csv`, columns by header name). */
export function parseCasaRegister(text: string): ParsedRegister {
  const { records } = byHeader(text, CASA_REQUIRED, 'CASA register');
  const rows: RegisterRow[] = [];
  const problems: string[] = [];
  for (const r of records) {
    const suffix = (r['Mark'] ?? '').trim().toUpperCase();
    if (!/^[A-Z0-9]{2,4}$/.test(suffix)) {
      problems.push(`CASA row with the mark "${suffix}" left out: not a VH mark.`);
      continue;
    }
    const suspended = /suspend/i.test(r['suspendstatus'] ?? '') || /suspend/i.test(r['regType'] ?? '');
    rows.push({
      register: 'casa',
      mark: `VH-${suffix}`,
      icao_hex: vhHexFromMark(`VH-${suffix}`),
      manufacturer: manufacturerName(r['Manu']),
      model: blank(r['Model']),
      icao_type: blank(r['ICAOtypedesig']),
      airframe: airframeClass(r['Airframe']),
      airframe_text: blank(r['Airframe']),
      serial: blank(r['Serial']),
      year_built: year(r['Yearmanu']),
      mtow_kg: num(r['MTOW']),
      engine: engineText(r['engnum'] ?? '', r['Engmodel'] ?? '', r['Engtype'] ?? ''),
      status: suspended ? 'Suspended' : 'Registered',
      suspended,
      first_registered: dmy(r['Datefirstreg']),
      expires: dmy(r['Regexpirydate']),
      country: 'Australia',
      holder: party({
        name: r['regholdname'] ?? null,
        lines: [r['regholdadd1'], r['regholdadd2']],
        suburb: r['regholdSuburb'] ?? null,
        state: r['regholdState'] ?? null,
        postcode: r['regholdPostcode'] ?? null,
        country: r['regholdCountry'] ?? null,
      }),
      operator: party({
        name: r['regopName'] ?? null,
        lines: [r['regopadd1'], r['regopadd2']],
        suburb: r['regopSuburb'] ?? null,
        state: r['regopState'] ?? null,
        postcode: r['regopPostcode'] ?? null,
        country: r['regopCountry'] ?? null,
      }),
      raw: { ...r },
    });
  }
  return { rows, problems };
}

// ---------------------------------------------------------------------------------------------- NZ CAA

const NZ_REQUIRED = [
  'Model Category',
  'Registration Mark',
  'Registered on',
  'Manufacturer',
  'Model',
  'Serial No.',
  'MCTOW (Kg)',
  'Owner Name',
  'Owner Address',
  'Mode S Code HEX',
];

/** "PO Box 2240, TAUPO 3351, New Zealand" → town Taupo, postcode 3351, country New Zealand. */
function nzAddress(address: string): {
  suburb: string | null;
  postcode: string | null;
  country: string | null;
} {
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  let country: string | null = null;
  if (parts.length > 1 && !/\d/.test(parts[parts.length - 1]!)) country = parts.pop()!;
  const last = parts[parts.length - 1] ?? '';
  const m = /^(.*?)\s+(\d{4})$/.exec(last);
  if (!m) return { suburb: null, postcode: null, country };
  return { suburb: registerTitleCase(m[1]!.trim()), postcode: m[2]!, country };
}

/** The NZ CAA register CSV (`Aircraft-Register-for-website.csv`). */
export function parseNzRegister(text: string): ParsedRegister {
  const { records } = byHeader(text, NZ_REQUIRED, 'NZ CAA register');
  const rows: RegisterRow[] = [];
  const problems: string[] = [];
  for (const r of records) {
    const mark = normaliseMark(r['Registration Mark']);
    if (!mark || !mark.startsWith('ZK-')) {
      problems.push(`NZ CAA row "${r['Registration Mark'] ?? ''}" left out: not a ZK mark.`);
      continue;
    }
    const address = blank(r['Owner Address']);
    const where = address ? nzAddress(address) : { suburb: null, postcode: null, country: null };
    const owner = blank(r['Owner Name']);
    rows.push({
      register: 'nzcaa',
      mark,
      icao_hex: hexOf(r['Mode S Code HEX']),
      manufacturer: manufacturerName(r['Manufacturer']),
      model: blank(r['Model']),
      icao_type: null,
      airframe: airframeClass(r['Model Category']),
      airframe_text: blank(r['Model Category']),
      serial: blank(r['Serial No.']),
      year_built: null,
      mtow_kg: num(r['MCTOW (Kg)']),
      engine: null,
      status: 'Registered',
      suspended: false,
      first_registered: dmy(r['Registered on']),
      expires: null,
      country: 'New Zealand',
      holder: owner ? { name: owner, address, ...where, state: null } : null,
      operator: null,
      raw: { ...r },
    });
  }
  return { rows, problems };
}

// ---------------------------------------------------------------------------------------------- Transport Canada

export interface TccaLayout {
  /** carscurr.txt column names in order. */
  readonly aircraft: readonly string[];
  /** carsownr.txt column names in order. */
  readonly owners: readonly string[];
}

/** The column order of carscurr.txt and carsownr.txt from carslayout.txt (shipped in the same zip). */
export function parseTccaLayout(text: string): TccaLayout {
  const aircraft: string[] = [];
  const owners: string[] = [];
  let into: string[] | null = null;
  for (const line of text.split(/\r?\n/)) {
    const m = /^\s*\d+\s+(\S+)\s+\S+\s+(\S+)/.exec(line);
    if (!m) continue;
    if (/^CARSCURR\.TXT$/i.test(m[1]!)) into = aircraft;
    else if (/^CARSOWNR\.TXT$/i.test(m[1]!)) into = owners;
    else if (into) into.push(m[1]!.toUpperCase());
  }
  for (const [list, need] of [
    [aircraft, ['MARK', 'COMMON_NAME', 'MODEL_NAME', 'AIRCRAFT_CATEGORY_E', 'MODE_S_TRANSPONDER_BINARY']],
    [owners, ['MARK_LINK', 'FULL_NAME', 'CITY', 'COUNTRY_E']],
  ] as const) {
    const missing = need.filter((n) => !list.includes(n));
    if (missing.length)
      throw new Error(`The Transport Canada layout file has changed: it lacks ${missing.join(', ')}.`);
  }
  return { aircraft, owners };
}

function records(
  text: string,
  columns: readonly string[],
): { recs: Record<string, string>[]; stated?: number } {
  const recs: Record<string, string>[] = [];
  let stated: number | undefined;
  for (const r of registerCsvIter(text)) {
    if (r.length === 1) {
      const m = /^\s*(\d+) rows selected\.?\s*$/.exec(r[0]!);
      if (m) stated = Number(m[1]);
      continue;
    }
    if (r.length !== columns.length) continue;
    const o: Record<string, string> = {};
    columns.forEach((c, i) => (o[c] = (r[i] ?? '').trim()));
    recs.push(o);
  }
  return stated === undefined ? { recs } : { recs, stated };
}

/** A Canadian mark: three letters are the old CF- marks, four the C-F/C-G/C-I marks. */
function tccaMark(m: string): string | null {
  const t = m.trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(t)) return `CF-${t}`;
  if (/^[A-Z]{4}$/.test(t)) return `C-${t}`;
  return null;
}

/** Transport Canada's CCAR files: carscurr.txt (aircraft) joined to carsownr.txt (owners) by mark, latin-1 decoded. */
export function parseTccaRegister(carscurr: string, carsownr: string, layout: TccaLayout): ParsedRegister {
  const air = records(carscurr, layout.aircraft);
  const own = records(carsownr, layout.owners);
  const owners = new Map<string, Record<string, string>[]>();
  for (const o of own.recs) {
    const k = o['MARK_LINK']!.trim();
    owners.set(k, [...(owners.get(k) ?? []), o]);
  }
  const rows: RegisterRow[] = [];
  const problems: string[] = [];
  for (const a of air.recs) {
    const mark = tccaMark(a['MARK'] ?? '');
    if (!mark) {
      problems.push(`Transport Canada row "${a['MARK'] ?? ''}" left out: not a Canadian mark.`);
      continue;
    }
    const os = owners.get((a['MARK'] ?? '').trim()) ?? [];
    const first = os.find((o) => o['ACTIVE_FLAG'] === 'A') ?? os[0];
    const bits = (a['MODE_S_TRANSPONDER_BINARY'] ?? '').trim();
    const hex = /^[01]{24}$/.test(bits) ? parseInt(bits, 2).toString(16).padStart(6, '0') : null;
    const status = blank(a['REGISTRATION_AUTH_STATUS_E']);
    rows.push({
      register: 'tcca',
      mark,
      icao_hex: hex,
      manufacturer: blank(a['COMMON_NAME']) ?? manufacturerName(a['ID_PLATE_MANUFACTURERS_NAME']),
      model: blank(a['MODEL_NAME']),
      icao_type: null,
      airframe: airframeClass(a['AIRCRAFT_CATEGORY_E']),
      airframe_text: blank(a['AIRCRAFT_CATEGORY_E']),
      serial: blank(a['MANUFACTURERS_SERIAL_NUMBER']),
      year_built: year(a['DATE_MANUFACTURE_ASSEMBLY']),
      mtow_kg: num(a['AIR_WEIGHT_KILOS']),
      engine: blank(a['ENGINE_CATEGORY_E'])
        ? `${num(a['NUMBER_OF_ENGINES']) ?? 1} × ${a['ENGINE_CATEGORY_E']!.toLowerCase()}`
        : null,
      status,
      suspended: false,
      first_registered: ymd(a['ISSUE_DATE']),
      expires: ymd(a['INEFFECTIVE_DATE']),
      country: 'Canada',
      holder: first
        ? party({
            name: first['FULL_NAME'] ?? null,
            lines: [first['STREET_NAME'], first['STREET_NAME2']],
            suburb: first['CITY'] ?? null,
            state: first['PROVINCE_OR_STATE_E'] ?? null,
            postcode: first['POSTAL_CODE'] ?? null,
            country: first['COUNTRY_E'] ?? null,
          })
        : null,
      operator: null,
      raw: { aircraft: { ...a }, owners: os.map((o) => ({ ...o })) },
    });
  }
  return air.stated === undefined ? { rows, problems } : { rows, problems, statedRows: air.stated };
}

// ---------------------------------------------------------------------------------------------- FAA

const FAA_AIRCRAFT_TYPE: Readonly<Record<string, string>> = {
  '1': 'Glider',
  '2': 'Balloon',
  '3': 'Blimp/Dirigible',
  '4': 'Fixed wing single engine',
  '5': 'Fixed wing multi engine',
  '6': 'Rotorcraft',
  '7': 'Weight-shift-control',
  '8': 'Powered parachute',
  '9': 'Gyroplane',
  H: 'Hybrid lift',
  O: 'Other',
};

const FAA_STATUS: Readonly<Record<string, string>> = {
  V: 'Registered',
  M: 'Registered (dealer)',
  T: 'Registered (trainee)',
  R: 'Registration pending',
  E: 'Revoked by the FAA',
  D: 'Expired dealer',
  X: 'Revoked',
};

/**
 * The FAA's releasable aircraft database: MASTER.txt joined to ACFTREF.txt (make and model by code), columns by
 * header name (the files carry a header row; values are padded). A registrant who asked the FAA to withhold their
 * details is blank in the file and stays blank.
 */
export function parseFaaRegister(master: string, acftref: string): ParsedRegister {
  const ref = byHeader(acftref, ['CODE', 'MFR', 'MODEL'], 'FAA ACFTREF');
  const models = new Map(ref.records.map((r) => [r['CODE']!.trim(), r]));
  const { records: recs } = byHeader(
    master,
    ['N-NUMBER', 'SERIAL NUMBER', 'MFR MDL CODE', 'NAME', 'TYPE AIRCRAFT', 'MODE S CODE HEX'],
    'FAA MASTER',
  );
  const rows: RegisterRow[] = [];
  const problems: string[] = [];
  for (const r0 of recs) {
    const r: Record<string, string> = {};
    for (const [k, v] of Object.entries(r0)) if (k) r[k] = v.trim();
    const n = (r['N-NUMBER'] ?? '').toUpperCase();
    if (!/^[1-9][0-9A-Z]{0,4}$/.test(n)) {
      problems.push(`FAA row "${n}" left out: not an N-number.`);
      continue;
    }
    const m = models.get(r['MFR MDL CODE'] ?? '');
    const typeText = FAA_AIRCRAFT_TYPE[r['TYPE AIRCRAFT'] ?? ''] ?? null;
    const status = r['STATUS CODE'] ? (FAA_STATUS[r['STATUS CODE']] ?? r['STATUS CODE']) : null;
    rows.push({
      register: 'faa',
      mark: `N${n}`,
      icao_hex: hexOf(r['MODE S CODE HEX']),
      manufacturer: m ? manufacturerName(m['MFR']) : null,
      model: m ? blank(m['MODEL']) : null,
      icao_type: null,
      airframe: airframeClass(typeText),
      airframe_text: typeText,
      serial: blank(r['SERIAL NUMBER']),
      year_built: year(r['YEAR MFR']),
      mtow_kg: null,
      engine: null,
      status,
      suspended: false,
      first_registered: ymd(r['CERT ISSUE DATE']),
      expires: ymd(r['EXPIRATION DATE']),
      country: 'United States of America',
      holder: party({
        name: r['NAME'] ?? null,
        lines: [r['STREET'], r['STREET2']],
        suburb: r['CITY'] ?? null,
        state: r['STATE'] ?? null,
        postcode: r['ZIP CODE'] ?? null,
        country: r['COUNTRY'] ?? null,
      }),
      operator: null,
      raw: m ? { master: r, model: { ...m } } : { master: r },
    });
  }
  return { rows, problems };
}

// ---------------------------------------------------------------------------------------------- CASA PNG (PDF)

/** One piece of text of a PDF page as a PDF reader gives it (x and y from the bottom left, points). */
export interface PdfTextItem {
  readonly page: number;
  readonly x: number;
  readonly y: number;
  readonly str: string;
}

const PNG_COLUMNS = ['REG', 'MAKE', 'MODEL', 'OPERATOR_NAME', 'OPERATOR_ADDRESS'] as const;
const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** The edition date printed at the top of the PNG register ("Thursday, 13 August 2026"), or null. */
export function pngRegisterEdition(items: readonly PdfTextItem[]): string | null {
  for (const it of items) {
    const m = /(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/.exec(it.str);
    const mi = m ? MONTHS.indexOf(m[2]!.toLowerCase()) : -1;
    if (m && mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
  }
  return null;
}

/**
 * The CASA PNG register PDF: a table per page with the header REG, MAKE, MODEL, OPERATOR_NAME, OPERATOR_ADDRESS.
 * Cells are top-aligned, so each row starts at its P2- mark and every other piece of text belongs to the nearest row
 * at or above it in its column (an address runs over two or three lines). The columns are found from each page's own
 * header, so a shifted layout still reads; text above the first row of a page continues the previous page's last
 * row. A mark printed with gaps ("P2-H S I") is read without them. The total printed under the last row is returned
 * as `statedRows`.
 */
export function parsePngRegister(items: readonly PdfTextItem[]): ParsedRegister {
  const pages = [...new Set(items.map((i) => i.page))].sort((a, b) => a - b);
  type Row = { cells: Record<(typeof PNG_COLUMNS)[number], string[]>; page: number };
  const out: Row[] = [];
  const problems: string[] = [];
  let statedRows: number | undefined;
  for (const page of pages) {
    const on = items.filter((i) => i.page === page && i.str.trim() !== '');
    const head = PNG_COLUMNS.map((c) => on.find((i) => i.str.trim() === c));
    if (head.some((h) => !h)) {
      problems.push(`Page ${page} has no REG … OPERATOR_ADDRESS header: left out.`);
      continue;
    }
    const headY = Math.min(...head.map((h) => h!.y));
    const starts = head.map((h) => h!.x);
    const column = (x: number): number => {
      let c = 0;
      starts.forEach((s, i) => {
        if (x >= s - 3) c = i;
      });
      return c;
    };
    const body = on
      .filter((i) => i.y < headY - 2 && !/^Page \d+ of \d+$/i.test(i.str.trim()))
      .sort((a, b) => b.y - a.y || a.x - b.x);
    const anchors: { y: number; row: Row }[] = [];
    for (const it of body) {
      if (column(it.x) !== 0) continue;
      const mark = it.str.replace(/\s+/g, '');
      if (/^P2-?[A-Z0-9]{2,4}$/i.test(mark)) {
        const row: Row = {
          page,
          cells: { REG: [mark], MAKE: [], MODEL: [], OPERATOR_NAME: [], OPERATOR_ADDRESS: [] },
        };
        anchors.push({ y: it.y, row });
        out.push(row);
      } else if (/^\d+$/.test(mark)) statedRows = Number(mark);
      else problems.push(`Page ${page}: "${mark}" in the REG column is not a P2 mark: left out.`);
    }
    for (const it of body) {
      const c = column(it.x);
      if (c === 0) continue;
      // the nearest row at or above (anchors run top to bottom); above the page's first row: the previous page's last
      const anchor = [...anchors].reverse().find((a) => a.y >= it.y - 1.5);
      const row = anchor?.row ?? out[out.length - anchors.length - 1];
      if (!row) {
        problems.push(`Page ${page}: "${it.str.trim()}" belongs to no aircraft: left out.`);
        continue;
      }
      row.cells[PNG_COLUMNS[c]!].push(it.str.trim());
    }
  }
  const rows: RegisterRow[] = [];
  for (const r of out) {
    const mark = normaliseMark(r.cells.REG[0]!);
    const text = (k: (typeof PNG_COLUMNS)[number]): string | null => blank(r.cells[k].join(' '));
    if (!mark) continue;
    const make = text('MAKE');
    const model = text('MODEL');
    const name = text('OPERATOR_NAME');
    if (!make || !model || !name)
      problems.push(`${mark} (page ${r.page}) has an empty make, model or operator.`);
    rows.push({
      register: 'casapng',
      mark,
      icao_hex: null,
      manufacturer: manufacturerName(make),
      model,
      icao_type: null,
      airframe: null,
      airframe_text: null,
      serial: null,
      year_built: null,
      mtow_kg: null,
      engine: null,
      status: 'Registered',
      suspended: false,
      first_registered: null,
      expires: null,
      country: 'Papua New Guinea',
      holder: null,
      operator: name
        ? {
            name: registerTitleCase(name),
            address: text('OPERATOR_ADDRESS'),
            suburb: null,
            state: null,
            postcode: null,
            country: null,
          }
        : null,
      raw: Object.fromEntries(PNG_COLUMNS.map((k) => [k, r.cells[k].join('\n')])),
    });
  }
  return statedRows === undefined ? { rows, problems } : { rows, problems, statedRows };
}

/** A new edition far smaller than the last is refused and the last kept (a changed layout reads few rows). */
export function checkEditionSize(register: RegisterId, rows: number, previous: number | null): string | null {
  const name = registerSource(register).name;
  if (rows === 0) return `${name}: the new file has no aircraft. The last edition is kept.`;
  if (previous !== null && rows < previous * 0.8)
    return `${name}: the new file has ${rows} aircraft, against ${previous} in the last edition. The last edition is kept until someone checks the file.`;
  return null;
}

// ---------------------------------------------------------------------------------------------- marks

/** A registration as the registers write it, or null (RAAus marks are not registrations: see `raausMark`). */
export function normaliseMark(raw: string | null | undefined): string | null {
  const s = (raw ?? '').toUpperCase().replace(/[\s.]/g, '');
  if (!s) return null;
  if (raausMark(s)) return null;
  let m = /^VH-?([A-Z0-9]{3})$/.exec(s);
  if (m) return `VH-${m[1]}`;
  if (/^[A-Z]{3}$/.test(s)) return `VH-${s}`;
  m = /^ZK-?([A-Z0-9]{3})$/.exec(s);
  if (m) return `ZK-${m[1]}`;
  m = /^P2-?([A-Z0-9]{3})$/.exec(s);
  if (m) return `P2-${m[1]}`;
  m = /^N-?([1-9][0-9A-Z]{0,4})$/.exec(s);
  if (m) return `N${m[1]}`;
  m = /^CF-?([A-Z]{3})$/.exec(s);
  if (m) return `CF-${m[1]}`;
  m = /^C-?([FGI][A-Z]{3})$/.exec(s);
  if (m) return `C-${m[1]}`;
  if (/^[A-Z0-9]{1,2}-[A-Z0-9]{1,5}$/.test(s)) return s;
  return null;
}

export function registerForMark(mark: string): RegisterId | null {
  if (mark.startsWith('VH-')) return 'casa';
  if (mark.startsWith('ZK-')) return 'nzcaa';
  if (mark.startsWith('P2-')) return 'casapng';
  if (/^N[1-9]/.test(mark)) return 'faa';
  if (/^C-[FGI]|^CF-/.test(mark)) return 'tcca';
  return null;
}

/** A Recreational Aviation Australia mark (`24-5000`; written with a dash, a space or none), or null. */
export function raausMark(raw: string | null | undefined): string | null {
  const m = /^(10|19|23|24|25|28|32|55)[- ]?(\d{3,4})$/.exec((raw ?? '').trim());
  return m ? `${m[1]}-${m[2]}` : null;
}

// ---------------------------------------------------------------------------------------------- Australian hex

const B36 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const VH_BLOCK = 0x7c0000;
const VH_LAST = VH_BLOCK + 36 ** 3 - 1;

/**
 * The ICAO 24-bit address of an Australian mark: 0x7C0000 + 1296·a + 36·b + c, each character A–Z = 0–25 and
 * 0–9 = 26–35. CASA's file has no hex column; checked on 20 Oct 2026 against adsbdb's hex for 37 VH aircraft (every
 * first letter A to Z, and VH-82A and VH-8TH with digits): all exact (tests/fixtures/registers/adsbdb/vh-mode-s.csv).
 * Digit marks rest on two aircraft only. A feed's own hex always wins over this one.
 */
export function vhHexFromMark(mark: string): string | null {
  const m = /^VH-([A-Z0-9]{3})$/.exec(mark);
  if (!m) return null;
  const [a, b, c] = [...m[1]!].map((ch) => B36.indexOf(ch));
  return (VH_BLOCK + 1296 * a! + 36 * b! + c!).toString(16).padStart(6, '0');
}

/** The VH mark of an address in the Australian block that the rule above gives, or null. */
export function vhMarkFromHex(hex: string): string | null {
  const t = hexOf(hex);
  if (!t) return null;
  const n = parseInt(t, 16);
  if (n < VH_BLOCK || n > VH_LAST) return null;
  const v = n - VH_BLOCK;
  return `VH-${B36[Math.floor(v / 1296)]}${B36[Math.floor(v / 36) % 36]}${B36[v % 36]}`;
}

/** RAAus's public registration search (decision 329: opened in the browser, never called). */
export const RAAUS_SEARCH_URL = 'https://raaus.com.au/members/registration-search/';

export interface LookupInput {
  readonly registration?: string | null;
  readonly icaoHex?: string | null;
  readonly callsign?: string | null;
  readonly lat?: number | null;
  readonly lon?: number | null;
}

export interface LookupKey {
  /** The cache key: `reg:<mark>`, `hex:<hex>` or `raaus:<mark>`. */
  readonly key: string;
  readonly mark: string | null;
  readonly hex: string | null;
  readonly raaus: string | null;
}

const AIRLINE_FLIGHT = /^[A-Z]{3}\d{1,4}[A-Z]?$/;
const inAustralia = (lat?: number | null, lon?: number | null): boolean =>
  typeof lat === 'number' && typeof lon === 'number' && lat >= -45 && lat <= -9 && lon >= 110 && lon <= 160;

/**
 * What to look up for an aircraft (AIRCRAFT_OWNERSHIP.md step 1, with Teslyn's data): its registration and hex; a
 * callsign that reads as a registration; a bare three-letter callsign inside Australia as VH-. Airline flight numbers
 * (QFA123) are skipped. Null when there is nothing to look up.
 */
export function aircraftLookupKey(a: LookupInput): LookupKey | null {
  const raaus = raausMark(a.registration) ?? raausMark(a.callsign);
  const hex = hexOf((a.icaoHex ?? '').replace(/^~/, ''));
  let mark = normaliseMark(a.registration);
  if (!mark && a.callsign) {
    const c = a.callsign.trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(c)) mark = inAustralia(a.lat, a.lon) ? `VH-${c}` : null;
    else if (!AIRLINE_FLIGHT.test(c) && (c.includes('-') || /^(VH|ZK|P2|N[1-9]|C[FGI])/.test(c)))
      mark = normaliseMark(c);
  }
  if (mark) return { key: `reg:${mark}`, mark, hex, raaus: null };
  if (hex) return { key: `hex:${hex}`, mark: vhMarkFromHex(hex), hex, raaus: null };
  if (raaus) return { key: `raaus:${raaus}`, mark: null, hex: null, raaus };
  return null;
}

// ---------------------------------------------------------------------------------------------- profiles

export interface AircraftProfile {
  /** The lookup key it answers (`aircraftLookupKey`). */
  readonly key: string;
  readonly found: boolean;
  readonly registration: string | null;
  readonly icaoHex: string | null;
  readonly manufacturer: string | null;
  readonly model: string | null;
  readonly icaoType: string | null;
  readonly airframe: AirframeClass | null;
  readonly serial: string | null;
  readonly yearBuilt: number | null;
  readonly mtowKg: number | null;
  readonly engine: string | null;
  readonly status: string | null;
  readonly suspended: boolean;
  readonly firstRegistered: string | null;
  /** The country of registration. */
  readonly country: string | null;
  /** Tenant staff only (decision 327). */
  readonly holder: RegisterParty | null;
  /** Tenant staff only, and only when it is not the holder. */
  readonly operator: RegisterParty | null;
  readonly ownersShown: boolean;
  readonly source: {
    readonly kind: RegisterId | 'adsbdb';
    readonly name: string;
    readonly edition: string | null;
  } | null;
  /** A recreational (RAAus) mark: no public register data, a "Search on RAAus" link. */
  readonly raaus: boolean;
  /** Why nothing could be found right now (the lookup should be tried again), or null. */
  readonly error: string | null;
  readonly fetchedAt: string;
}

export const PROFILE_FOUND_TTL_MS = 30 * 86_400_000;
export const PROFILE_MISS_TTL_MS = 86_400_000;

/** When a profile asked at `nowMs` goes stale: 30 days when found, 24 hours when nothing was found. */
export function profileExpiresAt(found: boolean, nowMs: number): number {
  return nowMs + (found ? PROFILE_FOUND_TTL_MS : PROFILE_MISS_TTL_MS);
}

/**
 * Staff of the tenant see owners and operators; clients never (decision 327). Someone who is both a client and on the
 * tenant's staff is staff. The database applies the same rule (`aircraft_register_lookup`).
 */
export function ownersVisible(staffRoles: readonly string[]): boolean {
  return staffRoles.length > 0;
}

const sameParty = (a: RegisterParty | null, b: RegisterParty | null): boolean =>
  !!a && !!b && a.name.toLowerCase().replace(/\W/g, '') === b.name.toLowerCase().replace(/\W/g, '');

/** A register row as the card's profile. Owners are kept only when `ownersShown` (the database said staff). */
export function profileFromRegister(
  row: RegisterRow,
  edition: string,
  ownersShown: boolean,
  key: string,
  fetchedAt: string,
): AircraftProfile {
  const holder = ownersShown ? row.holder : null;
  const operator = ownersShown && row.operator && !sameParty(row.holder, row.operator) ? row.operator : null;
  return {
    key,
    found: true,
    registration: row.mark,
    icaoHex: row.icao_hex,
    manufacturer: row.manufacturer,
    model: row.model,
    icaoType: row.icao_type,
    airframe: row.airframe,
    serial: row.serial,
    yearBuilt: row.year_built,
    mtowKg: row.mtow_kg,
    engine: row.engine,
    status: row.status,
    suspended: row.suspended,
    firstRegistered: row.first_registered,
    country: row.country,
    holder: holder ?? null,
    operator: operator ?? null,
    ownersShown,
    source: { kind: row.register, name: registerSource(row.register).name, edition },
    raaus: false,
    error: null,
    fetchedAt,
  };
}

/** The same profile for someone who may not see owners. */
export function stripOwners(p: AircraftProfile): AircraftProfile {
  return { ...p, holder: null, operator: null, ownersShown: false };
}

/** Nothing found (or a RAAus mark, or a failed lookup with its words). */
export function emptyProfile(
  key: string,
  fetchedAt: string,
  raaus = false,
  error: string | null = null,
): AircraftProfile {
  return {
    key,
    found: false,
    registration: key.startsWith('reg:') ? key.slice(4) : key.startsWith('raaus:') ? key.slice(6) : null,
    icaoHex: key.startsWith('hex:') ? key.slice(4) : null,
    manufacturer: null,
    model: null,
    icaoType: null,
    airframe: null,
    serial: null,
    yearBuilt: null,
    mtowKg: null,
    engine: null,
    status: null,
    suspended: false,
    firstRegistered: null,
    country: null,
    holder: null,
    operator: null,
    ownersShown: false,
    source: null,
    raaus,
    error,
    fetchedAt,
  };
}

// ---------------------------------------------------------------------------------------------- adsbdb

/** Teslyn names itself and a contact on every adsbdb request (decision 333; never a browser's User-Agent). */
export const ADSBDB_USER_AGENT = 'Teslyn/2026.10 (Braynewave aircraft details; +mailto:braynewave@gmail.com)';
export const ADSBDB_URL = 'https://api.adsbdb.com/v0/aircraft/';
/** adsbdb calls one aircraft_details request may make; the rest answer "try again" (lookups happen on a click). */
export const ADSBDB_CALLS_PER_REQUEST = 5;

export interface AdsbdbAircraft {
  readonly registration: string | null;
  readonly icaoHex: string | null;
  readonly manufacturer: string | null;
  readonly type: string | null;
  readonly icaoType: string | null;
  readonly country: string | null;
}

/** An adsbdb answer: the aircraft, or null for its 404 "unknown aircraft". Any other answer throws. */
export function parseAdsbdb(status: number, body: string): AdsbdbAircraft | null {
  if (status === 404) return null;
  if (status !== 200) throw new Error(`adsbdb answered HTTP ${status}.`);
  let j: unknown;
  try {
    j = JSON.parse(body);
  } catch {
    throw new Error('adsbdb sent an answer Teslyn cannot read.');
  }
  const a = (j as { response?: { aircraft?: Record<string, unknown> } }).response?.aircraft;
  if (!a || typeof a !== 'object') return null;
  const s = (k: string): string | null =>
    typeof a[k] === 'string' && (a[k] as string).trim() ? (a[k] as string).trim() : null;
  return {
    // adsbdb writes some Canadian marks with a "CA-" prefix (C005C5 → "CA-FCEU")
    registration:
      normaliseMark((s('registration') ?? '').replace(/^CA-([FGI][A-Z]{3})$/, 'C-$1')) ?? s('registration'),
    icaoHex: hexOf(s('mode_s')),
    manufacturer: s('manufacturer'),
    type: s('type'),
    icaoType: s('icao_type'),
    country: s('registered_owner_country_name'),
  };
}

/** An adsbdb aircraft as a profile: type and country only, never the owner (decision 330). */
export function profileFromAdsbdb(a: AdsbdbAircraft, key: string, fetchedAt: string): AircraftProfile {
  return {
    ...emptyProfile(key, fetchedAt),
    found: true,
    registration: a.registration,
    icaoHex: a.icaoHex,
    manufacturer: a.manufacturer,
    model: a.type,
    icaoType: a.icaoType,
    country: a.country,
    source: { kind: 'adsbdb', name: 'adsbdb', edition: null },
  };
}

// ---------------------------------------------------------------------------------------------- the card

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function longDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1]} ${m[1]}` : iso;
}

function partyLine(p: RegisterParty, registerCountry: string | null): string {
  const place = [p.suburb, p.state].filter(Boolean).join(' ');
  const abroad = p.country && registerCountry && p.country !== registerCountry ? p.country : null;
  return [p.name, [place, abroad].filter(Boolean).join(', ')].filter(Boolean).join(', ');
}

/** The source line under the card: the register and its edition, or adsbdb's secondary data. */
export function sourceLine(p: AircraftProfile): string {
  if (!p.source)
    return p.raaus ? 'Recreational aircraft (RAAus): no public register data' : 'No register data';
  if (p.source.kind === 'adsbdb') return 'adsbdb (secondary data, type and country only)';
  return `${p.source.name}${p.source.edition ? `, edition ${longDate(p.source.edition)}` : ''}`;
}

/** The card's rows, label and value, all plain text (never markup). */
export function aircraftCardRows(p: AircraftProfile): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  const add = (label: string, value: string | null | undefined): void => {
    if (value) rows.push({ label, value });
  };
  add('Registration', p.registration);
  add('ICAO hex', p.icaoHex?.toUpperCase());
  const type = [p.manufacturer, p.model].filter(Boolean).join(' ');
  add('Aircraft', type ? `${type}${p.icaoType ? ` (${p.icaoType})` : ''}` : p.icaoType);
  add('Class', p.airframe ? AIRFRAME_WORD[p.airframe] : null);
  add('Serial', p.serial);
  add('Year built', p.yearBuilt ? String(p.yearBuilt) : null);
  add('MTOW', p.mtowKg ? `${p.mtowKg.toLocaleString('en-AU')} kg` : null);
  add('Engine', p.engine);
  add('Status', p.suspended ? 'Suspended' : p.status && p.status !== 'Registered' ? p.status : null);
  add('First registered', p.firstRegistered ? longDate(p.firstRegistered) : null);
  add('Country', p.source?.kind === 'adsbdb' ? p.country : null);
  if (p.ownersShown && p.holder) add('Registration holder', partyLine(p.holder, p.country));
  if (p.ownersShown && p.operator) add('Operator', partyLine(p.operator, p.country));
  return rows;
}

// ---------------------------------------------------------------------------------------------- the database shape

/** A row as the `aircraft_register` table holds it (snake_case columns, holder and operator flattened). */
export function registerRowToDb(r: RegisterRow, edition: string): Record<string, unknown> {
  const flat = (prefix: string, p: RegisterParty | null): Record<string, unknown> => ({
    [`${prefix}_name`]: p?.name ?? null,
    [`${prefix}_address`]: p?.address ?? null,
    [`${prefix}_suburb`]: p?.suburb ?? null,
    [`${prefix}_state`]: p?.state ?? null,
    [`${prefix}_postcode`]: p?.postcode ?? null,
    [`${prefix}_country`]: p?.country ?? null,
  });
  return {
    register: r.register,
    mark: r.mark,
    icao_hex: r.icao_hex,
    manufacturer: r.manufacturer,
    model: r.model,
    icao_type: r.icao_type,
    airframe: r.airframe,
    airframe_text: r.airframe_text,
    serial: r.serial,
    year_built: r.year_built,
    mtow_kg: r.mtow_kg,
    engine: r.engine,
    status: r.status,
    suspended: r.suspended,
    first_registered: r.first_registered,
    expires: r.expires,
    country: r.country,
    ...flat('holder', r.holder),
    ...flat('operator', r.operator),
    raw: r.raw,
    edition,
  };
}

/** The inverse of `registerRowToDb` (what `aircraft_register_lookup` answers; owners may be absent for clients). */
export function registerRowFromDb(d: Readonly<Record<string, unknown>>): {
  row: RegisterRow;
  edition: string;
} {
  const s = (k: string): string | null => (typeof d[k] === 'string' ? (d[k] as string) : null);
  const n = (k: string): number | null => (d[k] === null || d[k] === undefined ? null : Number(d[k]));
  const p = (prefix: string): RegisterParty | null =>
    s(`${prefix}_name`)
      ? {
          name: s(`${prefix}_name`)!,
          address: s(`${prefix}_address`),
          suburb: s(`${prefix}_suburb`),
          state: s(`${prefix}_state`),
          postcode: s(`${prefix}_postcode`),
          country: s(`${prefix}_country`),
        }
      : null;
  const date = (k: string): string | null => (s(k) ? s(k)!.slice(0, 10) : null);
  return {
    row: {
      register: s('register') as RegisterId,
      mark: s('mark')!,
      icao_hex: s('icao_hex'),
      manufacturer: s('manufacturer'),
      model: s('model'),
      icao_type: s('icao_type'),
      airframe: s('airframe') as AirframeClass | null,
      airframe_text: s('airframe_text'),
      serial: s('serial'),
      year_built: n('year_built'),
      mtow_kg: n('mtow_kg'),
      engine: s('engine'),
      status: s('status'),
      suspended: d['suspended'] === true,
      first_registered: date('first_registered'),
      expires: date('expires'),
      country: s('country') ?? '',
      holder: p('holder'),
      operator: p('operator'),
      raw: (d['raw'] as Record<string, unknown> | null) ?? {},
    },
    edition: date('edition') ?? '',
  };
}

// ---------------------------------------------------------------------------------------------- the operation

export interface AircraftDetailsDeps {
  readonly nowMs: number;
  /**
   * The register rows for these marks and hexes, as the caller may see them (`aircraft_register_lookup`, run with
   * the caller's own sign-in: the database leaves the owner columns out for anyone not on the tenant's staff).
   */
  registerLookup(
    marks: readonly string[],
    hexes: readonly string[],
  ): Promise<{
    readonly ownersShown: boolean;
    /** The registers with a current edition (an aircraft of a register not loaded goes to adsbdb). */
    readonly registers: readonly RegisterId[];
    readonly rows: readonly Readonly<Record<string, unknown>>[];
  }>;
  /** Fresh platform-cached adsbdb answers by key. */
  cacheGet(keys: readonly string[]): Promise<ReadonlyMap<string, AircraftProfile>>;
  cachePut(
    entries: readonly { key: string; found: boolean; profile: AircraftProfile; expiresAtMs: number }[],
  ): Promise<void>;
  /** GET adsbdb for one mark or hex with `ADSBDB_USER_AGENT`. */
  adsbdb(markOrHex: string): Promise<{ readonly status: number; readonly body: string }>;
  /** GET adsbdb's callsign API for one airline callsign with `ADSBDB_USER_AGENT` (decision 385). */
  adsbdbCallsign?(callsign: string): Promise<{ readonly status: number; readonly body: string }>;
}

/**
 * `aircraft_details`: one profile per aircraft asked, in order. Registers first (by mark, or by hex where the
 * register has one); adsbdb, cached for all of Teslyn, only for aircraft no loaded register can answer and only for
 * type and country; RAAus marks are never looked up. A registration adsbdb gives for a hex is looked up in the
 * registers again, so the register still wins.
 */
export async function aircraftDetails(
  inputs: readonly LookupInput[],
  deps: AircraftDetailsDeps,
): Promise<AircraftProfile[]> {
  const at = new Date(deps.nowMs).toISOString();
  const keys = inputs.map(aircraftLookupKey);
  const marks = [...new Set(keys.flatMap((k) => (k?.mark ? [k.mark] : [])))];
  const hexes = [...new Set(keys.flatMap((k) => (k?.hex ? [k.hex] : [])))];
  const found = new Map<string, AircraftProfile>();
  const loaded = new Set<RegisterId>();
  const fromRegister = async (ms: readonly string[], hs: readonly string[]): Promise<void> => {
    if (ms.length === 0 && hs.length === 0) return;
    const ans = await deps.registerLookup(ms, hs);
    for (const r of ans.registers) loaded.add(r);
    for (const d of ans.rows) {
      const { row, edition } = registerRowFromDb(d);
      const p = profileFromRegister(row, edition, ans.ownersShown, `reg:${row.mark}`, at);
      found.set(`reg:${row.mark}`, p);
      if (row.icao_hex) found.set(`hex:${row.icao_hex}`, p);
    }
  };
  await fromRegister(marks, hexes);
  const answer = (k: LookupKey): AircraftProfile | undefined =>
    (k.mark ? found.get(`reg:${k.mark}`) : undefined) ?? (k.hex ? found.get(`hex:${k.hex}`) : undefined);

  // adsbdb for what no register answered: foreign marks (no register loaded for the prefix) and bare hexes
  const ask = [
    ...new Set(
      keys.flatMap((k) => {
        if (!k || k.raaus || answer(k)) return [];
        if (k.mark) {
          const reg = registerForMark(k.mark);
          return reg === null || !loaded.has(reg) ? [k.key] : [];
        }
        return k.hex ? [k.key] : [];
      }),
    ),
  ];
  const cached = ask.length ? await deps.cacheGet(ask) : new Map<string, AircraftProfile>();
  const fresh: { key: string; found: boolean; profile: AircraftProfile; expiresAtMs: number }[] = [];
  const secondary = new Map<string, AircraftProfile>(cached);
  let calls = 0;
  for (const key of ask) {
    if (secondary.has(key)) continue;
    if (calls >= ADSBDB_CALLS_PER_REQUEST) {
      secondary.set(key, emptyProfile(key, at, false, 'Too many aircraft asked at once. Try again shortly.'));
      continue;
    }
    calls += 1;
    try {
      const res = await deps.adsbdb(key.replace(/^(reg|hex):/, ''));
      const a = parseAdsbdb(res.status, res.body);
      const p = a ? profileFromAdsbdb(a, key, at) : emptyProfile(key, at);
      secondary.set(key, p);
      fresh.push({ key, found: p.found, profile: p, expiresAtMs: profileExpiresAt(p.found, deps.nowMs) });
    } catch (e) {
      secondary.set(
        key,
        emptyProfile(key, at, false, `${e instanceof Error ? e.message : 'adsbdb failed.'} Try again later.`),
      );
    }
  }
  if (fresh.length) await deps.cachePut(fresh);

  // a registration adsbdb gave for a hex goes back to the registers
  const again = [...secondary.values()].flatMap((p) =>
    p.registration &&
    p.source?.kind === 'adsbdb' &&
    loaded.has(registerForMark(p.registration) as RegisterId) &&
    !found.has(`reg:${p.registration}`)
      ? [p.registration]
      : [],
  );
  if (again.length) await fromRegister([...new Set(again)], []);

  return keys.map((k, i) => {
    if (!k) return emptyProfile(`none:${i}`, at);
    if (k.raaus) return emptyProfile(k.key, at, true);
    const direct = answer(k);
    if (direct) return { ...direct, key: k.key };
    const s = secondary.get(k.key);
    if (s?.registration && found.has(`reg:${s.registration}`)) {
      const reg = found.get(`reg:${s.registration}`)!;
      return { ...reg, key: k.key, icaoHex: reg.icaoHex ?? s.icaoHex };
    }
    if (s) return s;
    return emptyProfile(k.key, at);
  });
}

// ---------------------------------------------------------------------------------------------- the request

export const AIRCRAFT_DETAILS_MAX = 50;

export interface AircraftDetailsRequest {
  readonly tenantId: string;
  readonly aircraft: readonly LookupInput[];
  /** Airline callsigns whose routes are asked (decision 385), up to `ROUTES_MAX`. */
  readonly callsigns: readonly string[];
}

/**
 * The aircraft-details function's request body, checked: `{ tenant_id, aircraft?: [{ registration?, icaoHex?, … }],
 * callsigns?: ['QFA583', …] }`, with at least one aircraft or callsign.
 */
export function parseAircraftDetailsRequest(
  body: unknown,
): { ok: true; value: AircraftDetailsRequest } | { ok: false; error: string } {
  const b = body as { tenant_id?: unknown; aircraft?: unknown; callsigns?: unknown } | null;
  if (!b || typeof b !== 'object') return { ok: false, error: 'Send a JSON object.' };
  if (typeof b.tenant_id !== 'string' || !/^[0-9a-f-]{36}$/i.test(b.tenant_id))
    return { ok: false, error: 'tenant_id must be the organisation id.' };
  const callsigns: string[] = [];
  if (b.callsigns !== undefined) {
    if (!Array.isArray(b.callsigns) || b.callsigns.length > ROUTES_MAX)
      return { ok: false, error: `callsigns must list up to ${ROUTES_MAX} airline callsigns.` };
    for (const c of b.callsigns) {
      const cs = typeof c === 'string' ? airlineCallsign(c) : null;
      if (!cs) return { ok: false, error: 'Each callsign must be an airline flight, such as QFA583.' };
      if (!callsigns.includes(cs)) callsigns.push(cs);
    }
  }
  if (b.aircraft === undefined && callsigns.length > 0)
    return { ok: true, value: { tenantId: b.tenant_id, aircraft: [], callsigns } };
  if (!Array.isArray(b.aircraft) || (b.aircraft.length === 0 && callsigns.length === 0))
    return { ok: false, error: 'aircraft must list at least one aircraft.' };
  if (b.aircraft.length > AIRCRAFT_DETAILS_MAX)
    return { ok: false, error: `Ask for up to ${AIRCRAFT_DETAILS_MAX} aircraft at once.` };
  const out: LookupInput[] = [];
  for (const a of b.aircraft as unknown[]) {
    if (!a || typeof a !== 'object') return { ok: false, error: 'Each aircraft must be an object.' };
    const o = a as Record<string, unknown>;
    const str = (k: string): string | null | undefined | false => {
      const v = o[k];
      if (v === undefined || v === null) return v as null | undefined;
      return typeof v === 'string' && v.length <= 16 ? v : false;
    };
    const coord = (k: string, max: number): number | null | undefined | false => {
      const v = o[k];
      if (v === undefined || v === null) return v as null | undefined;
      return typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= max ? v : false;
    };
    const registration = str('registration');
    const icaoHex = str('icaoHex');
    const callsign = str('callsign');
    const lat = coord('lat', 90);
    const lon = coord('lon', 180);
    if ([registration, icaoHex, callsign, lat, lon].includes(false))
      return {
        ok: false,
        error: 'An aircraft has a registration, hex, callsign or position Teslyn cannot read.',
      };
    const item: Record<string, unknown> = {};
    if (registration !== undefined) item['registration'] = registration;
    if (icaoHex !== undefined) item['icaoHex'] = icaoHex;
    if (callsign !== undefined) item['callsign'] = callsign;
    if (lat !== undefined) item['lat'] = lat;
    if (lon !== undefined) item['lon'] = lon;
    out.push(item as LookupInput);
  }
  return { ok: true, value: { tenantId: b.tenant_id, aircraft: out, callsigns } };
}

// ---------------------------------------------------------------------------------------------- airline routes

/**
 * Airline routes (MB decision 385): an airline flight's origin and destination from adsbdb's callsign API, asked by
 * Teslyn's server (`aircraft-details`) with Teslyn's own User-Agent and kept for all of Teslyn in
 * `aircraft_profile_cache` under `route:<callsign>` (7 days found, 24 hours not). FlightAware is never scraped. The
 * route is the airline's published one for that flight number, not this flight's filed plan, and the card says so.
 */
export const ADSBDB_CALLSIGN_URL = 'https://api.adsbdb.com/v0/callsign/';
export const ROUTES_MAX = 10;
export const ROUTE_FOUND_TTL_MS = 7 * 86_400_000;
export const ROUTE_SOURCE = 'Route: adsbdb (the airline’s published schedule, not this flight’s plan)';

/** An airline flight callsign (three-letter ICAO airline code and a flight number, QFA583), else null. */
export function airlineCallsign(c: string | null | undefined): string | null {
  const s = (c ?? '').trim().toUpperCase();
  return AIRLINE_FLIGHT.test(s) ? s : null;
}

export interface RouteAirport {
  readonly icao: string | null;
  readonly iata: string | null;
  readonly name: string | null;
  readonly municipality: string | null;
  readonly country: string | null;
  /** The airport's position from adsbdb, for the schedule line on the map (R160, decision 934). */
  readonly lat: number | null;
  readonly lon: number | null;
}

export interface FlightRoute {
  readonly callsign: string;
  readonly found: boolean;
  readonly airline: {
    readonly name: string | null;
    readonly icao: string | null;
    readonly iata: string | null;
  } | null;
  readonly origin: RouteAirport | null;
  readonly destination: RouteAirport | null;
  /** Why nothing could be found right now (try again later), or null. */
  readonly error: string | null;
  readonly fetchedAt: string;
}

/** adsbdb's callsign answer: the route, or null for "unknown callsign" (404) and "invalid callsign" (400). */
export function parseAdsbdbRoute(
  status: number,
  body: string,
): Pick<FlightRoute, 'airline' | 'origin' | 'destination'> | null {
  if (status === 404 || status === 400) return null;
  if (status !== 200) throw new Error(`adsbdb answered HTTP ${status}.`);
  let j: unknown;
  try {
    j = JSON.parse(body);
  } catch {
    throw new Error('adsbdb sent a route Teslyn cannot read.');
  }
  const r = (j as { response?: { flightroute?: Record<string, unknown> } }).response?.flightroute;
  if (!r || typeof r !== 'object') return null;
  const s = (o: unknown, k: string): string | null => {
    const v = o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : null;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  };
  const n = (o: unknown, k: string, max: number): number | null => {
    const v = o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : null;
    return typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= max ? v : null;
  };
  const airport = (o: unknown): RouteAirport | null =>
    o && typeof o === 'object'
      ? {
          icao: s(o, 'icao_code'),
          iata: s(o, 'iata_code'),
          name: s(o, 'name'),
          municipality: s(o, 'municipality'),
          country: s(o, 'country_name'),
          lat: n(o, 'latitude', 90),
          lon: n(o, 'longitude', 180),
        }
      : null;
  return {
    airline: r.airline
      ? { name: s(r.airline, 'name'), icao: s(r.airline, 'icao'), iata: s(r.airline, 'iata') }
      : null,
    origin: airport(r.origin),
    destination: airport(r.destination),
  };
}

const placeOf = (a: RouteAirport): string =>
  `${a.municipality ?? a.name ?? a.icao ?? a.iata ?? '?'}${a.icao ? ` (${a.icao})` : ''}`;

/** The card's route rows, plain text: the airline and "Perth (YPPH) → Dubai (OMDB)". */
export function routeCardRows(r: FlightRoute): { label: string; value: string }[] {
  if (!r.found) return [];
  const rows: { label: string; value: string }[] = [];
  const codes = [r.airline?.iata, r.airline?.icao].filter(Boolean).join(', ');
  if (r.airline?.name)
    rows.push({ label: 'Airline', value: codes ? `${r.airline.name} (${codes})` : r.airline.name });
  if (r.origin && r.destination)
    rows.push({ label: 'Route', value: `${placeOf(r.origin)} → ${placeOf(r.destination)}` });
  return rows;
}

const noRoute = (callsign: string, fetchedAt: string, error: string | null = null): FlightRoute => ({
  callsign,
  found: false,
  airline: null,
  origin: null,
  destination: null,
  error,
  fetchedAt,
});

/** The routes of airline callsigns: cached answers first, then adsbdb (at most `ADSBDB_CALLS_PER_REQUEST`). */
export async function flightRoutes(
  callsigns: readonly string[],
  deps: AircraftDetailsDeps,
): Promise<FlightRoute[]> {
  const at = new Date(deps.nowMs).toISOString();
  const wanted = callsigns.map(airlineCallsign);
  const keys = [...new Set(wanted.flatMap((c) => (c ? [`route:${c}`] : [])))];
  const cached = keys.length
    ? ((await deps.cacheGet(keys)) as unknown as ReadonlyMap<string, FlightRoute>)
    : new Map<string, FlightRoute>();
  // a route kept before the airports' positions were (R160) is asked again, so its schedule line can be drawn
  const placed = (a: RouteAirport | null): boolean =>
    typeof a?.lat === 'number' && typeof a?.lon === 'number';
  const got = new Map<string, FlightRoute>(
    [...cached].filter(([, r]) => !r.found || (placed(r.origin) && placed(r.destination))),
  );
  const fresh: { key: string; found: boolean; profile: AircraftProfile; expiresAtMs: number }[] = [];
  let calls = 0;
  for (const key of keys) {
    if (got.has(key)) continue;
    const cs = key.slice(6);
    if (!deps.adsbdbCallsign) {
      got.set(key, noRoute(cs, at, 'Airline routes are not available on Teslyn Cloud yet.'));
      continue;
    }
    if (calls >= ADSBDB_CALLS_PER_REQUEST) {
      got.set(key, noRoute(cs, at, 'Too many flights asked at once. Try again shortly.'));
      continue;
    }
    calls += 1;
    try {
      const res = await deps.adsbdbCallsign(cs);
      const r = parseAdsbdbRoute(res.status, res.body);
      const route: FlightRoute = r
        ? { callsign: cs, found: true, ...r, error: null, fetchedAt: at }
        : noRoute(cs, at);
      got.set(key, route);
      fresh.push({
        key,
        found: route.found,
        // the cache table keeps any JSON answer; routes share it under their own key
        profile: route as unknown as AircraftProfile,
        expiresAtMs: deps.nowMs + (route.found ? ROUTE_FOUND_TTL_MS : PROFILE_MISS_TTL_MS),
      });
    } catch (e) {
      got.set(key, noRoute(cs, at, `${e instanceof Error ? e.message : 'adsbdb failed.'} Try again later.`));
    }
  }
  if (fresh.length) await deps.cachePut(fresh);
  return wanted.map((c, i) => (c ? got.get(`route:${c}`)! : noRoute(callsigns[i] ?? '', at)));
}

/**
 * The aircraft-details function's whole answer: the profiles of the aircraft asked and the routes of the callsigns
 * asked. A request for routes only still checks, through the registers lookup, that the caller belongs to the
 * organisation (staff or client), and counts as one lookup of their minute.
 */
export async function aircraftDetailsAnswer(
  req: AircraftDetailsRequest,
  deps: AircraftDetailsDeps,
): Promise<{ profiles: AircraftProfile[]; routes: FlightRoute[] }> {
  if (req.aircraft.length === 0) await deps.registerLookup([], []);
  const profiles = req.aircraft.length ? await aircraftDetails(req.aircraft, deps) : [];
  const routes = req.callsigns.length ? await flightRoutes(req.callsigns, deps) : [];
  return { profiles, routes };
}
